/**
 * GlobalCommandBar.tsx — ⌘K command palette.
 *
 * Spotlight-style light panel on the zinc design system, following the
 * macOS-native conventions used across the app:
 * - Trigger: ⌘K on Apple platforms / Ctrl+K elsewhere (toggles — AppLayout)
 * - Every command executes (registry: components/commandBar/commands.ts)
 * - Recent commands persist across sessions (lib/commandRecents.ts)
 * - SPA navigation via react-router (no full-page reloads)
 * - ESC closes, focus is trapped while open, ↑↓ move + scroll into view,
 *   Enter executes, selection follows the pointer
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronRight } from 'lucide-react';

import { shortcutLabel } from '../lib/platform';
import { loadCommandRecents, pushCommandRecent } from '../lib/commandRecents';
import { useFocusTrap } from '../hooks/useFocusTrap';
import {
  COMMAND_GROUPS,
  buildCommandSuggestions,
  type CommandSuggestion,
} from './commandBar/commands';

/**
 * Anything can open the palette without prop drilling by dispatching this on
 * window — the same pattern SHOW_SHORTCUTS_EVENT already uses. The header
 * search box is the first caller: it used to be an input that stored state and
 * did nothing at all, while promising "Search customers, orders, products…".
 */
export const OPEN_COMMAND_BAR_EVENT = 'bbx:open-command-bar';

interface GlobalCommandBarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface CommandSection {
  title: string;
  items: CommandSuggestion[];
}

export const GlobalCommandBar: React.FC<GlobalCommandBarProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentIds, setRecentIds] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const selectedRef = useRef<HTMLButtonElement | null>(null);
  // Native dialog behavior: Tab cycles inside the palette, focus returns to
  // the previously focused element on close.
  const dialogRef = useFocusTrap(isOpen, onClose);

  const suggestions = buildCommandSuggestions(navigate);

  const filteredSuggestions = query.trim()
    ? suggestions.filter((suggestion) => {
        const searchTerms = query.toLowerCase().split(' ');
        return searchTerms.some(
          (term) =>
            suggestion.label.toLowerCase().includes(term) ||
            suggestion.keywords?.some((keyword) => keyword.includes(term)),
        );
      })
    : suggestions;

  // Sections: with a query, matches grouped by category; with no query,
  // persisted recents lead (the native palette convention), then everything.
  const sections: CommandSection[] = [];
  if (!query.trim() && recentIds.length > 0) {
    const recentItems = recentIds
      .map((id) => suggestions.find((s) => s.id === id))
      .filter((s): s is CommandSuggestion => Boolean(s));
    if (recentItems.length > 0) sections.push({ title: 'Recent', items: recentItems });
  }
  for (const { key, title } of COMMAND_GROUPS) {
    const items = filteredSuggestions.filter((s) => s.category === key);
    if (items.length > 0) sections.push({ title, items });
  }
  const flatRows = sections.flatMap((section) => section.items);

  const runSuggestion = (suggestion: CommandSuggestion): void => {
    // Actions may be async (create/export commands); they own their error
    // toasts, so the palette just fires and closes — like a native palette.
    void suggestion.action();
    setRecentIds(pushCommandRecent(suggestion.id));
    onClose();
  };

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, flatRows.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const selected = flatRows[selectedIndex];
        if (selected) runSuggestion(selected);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  // Focus input and load persisted recents when opened
  useEffect(() => {
    if (isOpen) {
      setRecentIds(loadCommandRecents());
      inputRef.current?.focus();
    }
  }, [isOpen]);

  // Keep the keyboard selection visible (native palettes follow the cursor).
  useEffect(() => {
    selectedRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [selectedIndex, query]);

  // Reset state when closed
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const renderRow = (
    suggestion: CommandSuggestion,
    flatIndex: number,
    sectionTitle: string,
  ): React.ReactElement => {
    const isSelected = flatIndex === selectedIndex;
    return (
      <button
        key={`${sectionTitle}-${suggestion.id}`}
        ref={isSelected ? selectedRef : undefined}
        onClick={() => runSuggestion(suggestion)}
        onMouseEnter={() => setSelectedIndex(flatIndex)}
        className={`w-full text-left px-3 py-2.5 rounded-md flex items-center gap-3 transition-colors ${
          isSelected ? 'bg-gray-100 text-gray-900' : 'text-gray-600 hover:bg-gray-50'
        }`}
      >
        <suggestion.icon
          className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-gray-900' : 'text-gray-400'}`}
        />
        <span className={`text-sm flex-1 ${isSelected ? 'font-medium' : ''}`}>
          {suggestion.label}
        </span>
        {isSelected && <ChevronRight className="w-4 h-4 text-gray-400" />}
      </button>
    );
  };

  let rowCursor = 0;

  return (
    <div
      className="fixed inset-0 bg-gray-950/25 backdrop-blur-sm z-command-bar flex items-start justify-center pt-32 bbx-scrim-in"
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Command bar"
        aria-keyshortcuts="Meta+K Control+K"
        className="w-full max-w-2xl bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden bbx-palette-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
          <Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            placeholder="What would you like to do?"
            spellCheck={false}
            autoCorrect="off"
            autoCapitalize="off"
            className="flex-1 bg-transparent border-none text-gray-900 placeholder:text-gray-400 focus:outline-none text-base"
          />
          <kbd className="px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs text-gray-500 font-medium">
            ESC
          </kbd>
        </div>

        {/* Suggestions list */}
        <div className="p-2 max-h-96 overflow-y-auto">
          {flatRows.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <Search className="w-8 h-8 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 text-sm">No commands found for &quot;{query}&quot;</p>
              <p className="text-gray-400 text-xs mt-1">
                Try something like &quot;create campaign&quot; or &quot;go to dashboard&quot;
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {sections.map((section) => {
                const start = rowCursor;
                rowCursor += section.items.length;
                return (
                  <div key={section.title}>
                    <div className="px-3 py-1.5">
                      <p className="text-xs uppercase tracking-wide font-medium text-gray-400">
                        {section.title}
                      </p>
                    </div>
                    <div className="space-y-1">
                      {section.items.map((item, i) => renderRow(item, start + i, section.title))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="border-t border-gray-200 px-4 py-2 bg-gray-50">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5">
                <kbd className="px-1.5 py-0.5 bg-white border border-gray-200 rounded font-medium">
                  ↑↓
                </kbd>
                Navigate
              </span>
              <span className="flex items-center gap-1.5">
                <kbd className="px-1.5 py-0.5 bg-white border border-gray-200 rounded font-medium">
                  Enter
                </kbd>
                Execute
              </span>
            </div>
            <span className="flex items-center gap-1.5">
              <kbd className="px-1.5 py-0.5 bg-white border border-gray-200 rounded font-medium">
                {shortcutLabel('K')}
              </kbd>
              Close
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
